import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useI18n } from '../../../i18n';
import type { SiteRow } from '../../../services/dutyBoardService';
import {
  MINUTES_PER_DAY,
  formatCoverageWindow,
  getCoverageDurationMinutes,
  getSiteCoverageWindow,
} from '../../../utils/siteScheduling';

export type ScheduleSiteOption = Pick<
  SiteRow,
  | 'id'
  | 'name'
  | 'client_contract_id'
  | 'client_contract_name'
  | 'required_coverage_start_minute'
  | 'required_coverage_end_minute'
  | 'required_guard_count'
  | 'required_licence'
>;

interface Props {
  sites: ScheduleSiteOption[];
  selectedIds: string[];
  onChange: (siteIds: string[]) => void;
  disabled?: boolean;
}

/** Past this many sites a search box earns its place. */
const SEARCH_THRESHOLD = 8;

interface ClientGroup {
  key: string;
  name: string;
  sites: ScheduleSiteOption[];
}

function GroupCheckbox({ checked, mixed, ...rest }: { checked: boolean; mixed: boolean } & React.InputHTMLAttributes<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return <input ref={ref} type="checkbox" checked={checked} aria-checked={mixed ? 'mixed' : checked} {...rest} />;
}

/** Which sites a run covers, grouped by client, with all, none and a whole client a click away. */
export default function SitePicker({ sites, selectedIds, onChange, disabled = false }: Props) {
  const { t, tn } = useI18n();
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const groups = useMemo<ClientGroup[]>(() => {
    const term = query.trim().toLowerCase();
    const byClient = new Map<string, ClientGroup>();
    sites
      .filter((site) => !term
        || site.name.toLowerCase().includes(term)
        || (site.client_contract_name ?? '').toLowerCase().includes(term))
      .forEach((site) => {
        const key = site.client_contract_id ?? '';
        const group = byClient.get(key) ?? { key, name: site.client_contract_name ?? '', sites: [] };
        group.sites.push(site);
        byClient.set(key, group);
      });
    return Array.from(byClient.values())
      .map((group) => ({ ...group, sites: [...group.sites].sort((a, b) => a.name.localeCompare(b.name)) }))
      // Sites with no client go last.
      .sort((a, b) => Number(a.key === '') - Number(b.key === '') || a.name.localeCompare(b.name));
  }, [query, sites]);

  const toggle = (ids: string[], on: boolean) => {
    const next = new Set(selected);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    // Keep the board's own order, so the choice reads the same however it was made.
    onChange(sites.filter((site) => next.has(site.id)).map((site) => site.id));
  };

  const siteMeta = (site: ScheduleSiteOption): string => {
    const window = getSiteCoverageWindow(site);
    const coverage = getCoverageDurationMinutes(window) >= MINUTES_PER_DAY ? t('24 hours') : formatCoverageWindow(site);
    const guards = tn('{count} guard', '{count} guards', site.required_guard_count ?? 1);
    return [coverage, guards, site.required_licence?.name].filter(Boolean).join(' · ');
  };

  return (
    <div className="asch-sites">
      <div className="asch-sites__bar">
        <span className="asch-sites__count" aria-live="polite">
          {t('{selected} of {total} sites selected', { selected: selectedIds.length, total: sites.length })}
        </span>
        <span className="asch-sites__actions">
          <button
            type="button"
            className="asch-link"
            onClick={() => onChange(sites.map((site) => site.id))}
            disabled={disabled || selectedIds.length === sites.length}
          >
            {t('Select all')}
          </button>
          <button
            type="button"
            className="asch-link"
            onClick={() => onChange([])}
            disabled={disabled || selectedIds.length === 0}
          >
            {t('Clear')}
          </button>
        </span>
      </div>

      {sites.length > SEARCH_THRESHOLD && (
        <label className="asch-sites__search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            className="form__input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('Search sites or clients')}
            aria-label={t('Search sites or clients')}
          />
        </label>
      )}

      <div className="asch-sites__list">
        {groups.length === 0 && <p className="asch-hint">{t('No sites match.')}</p>}
        {groups.map((group) => {
          const ids = group.sites.map((site) => site.id);
          const chosen = ids.filter((id) => selected.has(id)).length;
          const groupId = `asch-client-${group.key || 'none'}`;
          return (
            <fieldset key={group.key || 'none'} className="asch-client" aria-labelledby={groupId}>
              <label className="asch-client__head" id={groupId}>
                <GroupCheckbox
                  checked={chosen === ids.length}
                  mixed={chosen > 0 && chosen < ids.length}
                  onChange={(event) => toggle(ids, event.target.checked)}
                  disabled={disabled}
                />
                <span className="asch-client__name">{group.name || t('No client')}</span>
                <span className="asch-client__count">{chosen}/{ids.length}</span>
              </label>
              <ul className="asch-client__sites">
                {group.sites.map((site) => (
                  <li key={site.id}>
                    <label className="asch-site">
                      <input
                        type="checkbox"
                        checked={selected.has(site.id)}
                        onChange={(event) => toggle([site.id], event.target.checked)}
                        disabled={disabled}
                      />
                      <span className="asch-site__name">{site.name}</span>
                      <span className="asch-site__meta">{siteMeta(site)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}
